const DEFAULT_PLAYOUT_DELAY_SECONDS = 0.3;
const MIN_PLAYOUT_DELAY_SECONDS = 0.1;
const MAX_PLAYOUT_DELAY_SECONDS = 0.75;
const DEFAULT_SWITCH_TIMEOUT_MS = 12000;

export const VOICE_TRANSPORT_TURN_TLS = 'turn-tls';
export const VOICE_TRANSPORT_DIRECT = 'direct';
export const VOICE_TRANSPORT_TOPIC = 'budlee.voice.transport.v1';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const getVoicePlayoutDelaySeconds = (
  configuredValue = process.env.REACT_APP_VOICE_PLAYOUT_DELAY_SECONDS,
) => {
  if (String(configuredValue).trim().toLowerCase() === 'false') {
    return 0;
  }

  const parsed = Number(configuredValue);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_PLAYOUT_DELAY_SECONDS;
  }
  if (parsed <= 0) {
    return 0;
  }
  return clamp(parsed, MIN_PLAYOUT_DELAY_SECONDS, MAX_PLAYOUT_DELAY_SECONDS);
};

export const configureRemoteVoiceTrack = (
  track,
  configuredDelay = process.env.REACT_APP_VOICE_PLAYOUT_DELAY_SECONDS,
) => {
  const delaySeconds = getVoicePlayoutDelaySeconds(configuredDelay);
  if (delaySeconds > 0 && typeof track?.setPlayoutDelay === 'function') {
    track.setPlayoutDelay(delaySeconds);
  }
  return delaySeconds;
};

const sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export const normalizeVoiceTransport = (rawTransport) => {
  if (!rawTransport || typeof rawTransport !== 'object') {
    return { version: 1, mode: VOICE_TRANSPORT_DIRECT };
  }
  const mode = rawTransport.mode === VOICE_TRANSPORT_TURN_TLS
    ? VOICE_TRANSPORT_TURN_TLS
    : VOICE_TRANSPORT_DIRECT;
  return {
    version: 1,
    mode,
    handshakeId: String(rawTransport.handshake_id || ''),
    topic: String(rawTransport.topic || VOICE_TRANSPORT_TOPIC),
    policy: rawTransport.policy === 'require' ? 'require' : 'prefer',
    readyTimeoutMs: Math.min(
      15000,
      Math.max(3000, Number(rawTransport.ready_timeout_ms) || 10000),
    ),
  };
};

/**
 * Configure LiveKit room transport preferences before connecting.
 *
 * When mode is 'turn-tls', forces WebRTC PeerConnection to route exclusively
 * through authenticated TURN/TLS over TCP (port 443) candidates. This bypasses
 * local network/ISP UDP packet loss and traffic policing (which causes ~25%
 * RTP loss and broken synthetic audio on direct UDP routes).
 */
export const applyVoiceTransportPreferences = (room, rawTransport) => {
  const transport = normalizeVoiceTransport(rawTransport);
  if (transport.mode !== VOICE_TRANSPORT_TURN_TLS || !room) {
    return { ...transport, activeMode: VOICE_TRANSPORT_DIRECT };
  }

  const filterToTlsIceServers = (iceServers) => {
    if (!Array.isArray(iceServers)) return iceServers;
    const tlsIceServers = [];
    for (const server of iceServers) {
      const urls = (Array.isArray(server.urls) ? server.urls : [server.urls]).filter(
        (u) => typeof u === 'string' && (u.startsWith('turns:') || u.includes('transport=tcp'))
      );
      // Prefer turns: (TLS on port 443) if present
      const turnsUrls = urls.filter((u) => u.startsWith('turns:'));
      const chosenUrls = turnsUrls.length > 0 ? turnsUrls : urls;
      if (chosenUrls.length > 0) {
        tlsIceServers.push({ ...server, urls: chosenUrls });
      }
    }
    return tlsIceServers;
  };

  const hookEngine = (engine) => {
    if (!engine || typeof engine.makeRTCConfiguration !== 'function') return;
    engine.rtcConfig = { ...engine.rtcConfig, iceTransportPolicy: 'relay' };
    if (engine._budleeTlsHooked) return;
    engine._budleeTlsHooked = true;

    const originalMake = engine.makeRTCConfiguration.bind(engine);
    engine.makeRTCConfiguration = function (serverResponse) {
      const config = originalMake(serverResponse);
      config.iceTransportPolicy = 'relay';
      if (serverResponse && Array.isArray(config.iceServers)) {
        const tlsServers = filterToTlsIceServers(config.iceServers);
        if (tlsServers.length > 0) {
          config.iceServers = tlsServers;
        }
      }
      return config;
    };
  };

  if (room.options) {
    room.options.rtcConfig = { ...room.options.rtcConfig, iceTransportPolicy: 'relay' };
  }

  if (room.engine) {
    hookEngine(room.engine);
  }

  const originalRecreateEngine = room.recreateEngine?.bind(room);
  if (typeof originalRecreateEngine === 'function') {
    room.recreateEngine = function (...args) {
      const res = originalRecreateEngine(...args);
      if (room.engine) {
        hookEngine(room.engine);
      }
      return res;
    };
  }

  const originalMaybeCreateEngine = room.maybeCreateEngine?.bind(room);
  if (typeof originalMaybeCreateEngine === 'function') {
    room.maybeCreateEngine = function (...args) {
      const res = originalMaybeCreateEngine(...args);
      if (room.engine) {
        hookEngine(room.engine);
      }
      return res;
    };
  }

  return {
    ...transport,
    activeMode: VOICE_TRANSPORT_TURN_TLS,
  };
};


/**
 * Ask LiveKit Cloud to reconnect this room over TURN/TLS before tutor speech.
 *
 * livekit-client 2.20 has no public connect option that selects TCP/TLS from
 * the Cloud-provided ICE servers. Its own connection checker uses this server
 * protocol-switch operation. Keep it isolated here, capability-negotiated by
 * the token endpoint, and fail back to the still-connected direct route.
 */
export const prepareVoiceTransport = async (
  room,
  rawTransport,
  { switchTimeoutMs = DEFAULT_SWITCH_TIMEOUT_MS } = {},
) => {
  const transport = normalizeVoiceTransport(rawTransport);
  if (transport.mode !== VOICE_TRANSPORT_TURN_TLS) {
    return { ...transport, activeMode: VOICE_TRANSPORT_DIRECT, switched: false };
  }
  if (typeof room?.simulateScenario !== 'function') {
    return {
      ...transport,
      activeMode: VOICE_TRANSPORT_DIRECT,
      switched: false,
      error: 'LiveKit protocol switching is unavailable',
    };
  }

  let reconnecting = false;
  let reconnected = false;
  let disconnected = false;
  const onReconnecting = () => { reconnecting = true; };
  const onReconnected = () => { reconnected = true; };
  const onDisconnected = () => { disconnected = true; };

  const addRoomListener = (event, fn) => {
    if (typeof room?.on === 'function') {
      room.on(event, fn);
    } else if (typeof room?.addListener === 'function') {
      room.addListener(event, fn);
    }
  };

  const removeRoomListener = (event, fn) => {
    if (typeof room?.off === 'function') {
      room.off(event, fn);
    } else if (typeof room?.removeListener === 'function') {
      room.removeListener(event, fn);
    }
  };

  addRoomListener('reconnecting', onReconnecting);
  addRoomListener('reconnected', onReconnected);
  addRoomListener('disconnected', onDisconnected);

  try {
    await room.simulateScenario('force-tls');

    // This mirrors LiveKit's connection checker: a switch that does not need
    // a reconnect is considered complete after a short server round trip.
    const deadline = Date.now() + Math.max(2000, switchTimeoutMs);
    await sleep(1000);
    if (!reconnecting && room.state === 'connected') {
      return { ...transport, activeMode: VOICE_TRANSPORT_TURN_TLS, switched: true };
    }
    while (Date.now() < deadline) {
      if (reconnected && room.state === 'connected') {
        return { ...transport, activeMode: VOICE_TRANSPORT_TURN_TLS, switched: true };
      }
      if (disconnected) break;
      await sleep(100);
    }
    const message = 'LiveKit TURN/TLS reconnect did not complete in time';
    if (room.state === 'connected') {
      return {
        ...transport,
        activeMode: VOICE_TRANSPORT_DIRECT,
        switched: false,
        error: message,
      };
    }
    throw new Error(message);
  } catch (error) {
    if (room?.state === 'connected') {
      return {
        ...transport,
        activeMode: VOICE_TRANSPORT_DIRECT,
        switched: false,
        error: error?.message || String(error),
      };
    }
    throw error;
  } finally {
    removeRoomListener('reconnecting', onReconnecting);
    removeRoomListener('reconnected', onReconnected);
    removeRoomListener('disconnected', onDisconnected);
  }
};

const candidateSnapshot = (stats) => {
  if (!stats || typeof stats.forEach !== 'function') return null;
  const rows = new Map();
  stats.forEach((row) => rows.set(row.id, row));

  let selectedPair;
  rows.forEach((row) => {
    if (row.type === 'transport' && row.selectedCandidatePairId) {
      selectedPair = rows.get(row.selectedCandidatePairId) || selectedPair;
    }
    if (
      row.type === 'candidate-pair'
      && (row.selected || (row.nominated && row.state === 'succeeded'))
    ) {
      selectedPair = selectedPair || row;
    }
  });
  if (!selectedPair) return null;
  const local = rows.get(selectedPair.localCandidateId) || {};
  const remote = rows.get(selectedPair.remoteCandidateId) || {};
  const relayProtocol = String(local.relayProtocol || '').toLowerCase();
  const url = String(local.url || '');
  return {
    candidateType: local.candidateType || '',
    protocol: local.protocol || '',
    relayProtocol,
    url,
    remoteCandidateType: remote.candidateType || '',
    tls: relayProtocol === 'tls' || url.toLowerCase().startsWith('turns:'),
  };
};

export const getVoiceTransportSnapshot = async (track) => {
  if (typeof track?.getRTCStatsReport !== 'function') return null;
  try {
    return candidateSnapshot(await track.getRTCStatsReport());
  } catch (_) {
    return null;
  }
};

export const publishVoiceTransportReady = async (
  room,
  transportResult,
  { participantIdentity, trackSid, audioPlaybackReady, selectedPair } = {},
) => {
  if (!transportResult?.handshakeId || !participantIdentity) return false;
  const payload = new TextEncoder().encode(JSON.stringify({
    version: 1,
    type: 'ready',
    handshake_id: transportResult.handshakeId,
    mode: transportResult.activeMode,
    requested_mode: transportResult.mode,
    track_sid: trackSid || '',
    audio_playback_ready: Boolean(audioPlaybackReady),
    selected_pair: selectedPair || null,
    switch_error: transportResult.error || '',
  }));
  await room.localParticipant.publishData(payload, {
    reliable: true,
    topic: transportResult.topic || VOICE_TRANSPORT_TOPIC,
    destinationIdentities: [participantIdentity],
  });
  return true;
};
